import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { createElement } from "react";
import { AppLayout } from "./components/AppLayout.js";
import { AuthGuard } from "./components/AuthGuard.js";
import { RequireAdmin } from "./components/RequireAdmin.js";
import { RequireRole } from "./components/RequireRole.js";
import { BcgPage, validateBcgSearch } from "./routes/bcg.js";
import { KpiAusreisserPage } from "./routes/kpi/ausreisser.js";
import { KpiBereichPage } from "./routes/kpi/bereich.js";
import { KpiPortfolioPage } from "./routes/kpi/portfolio.js";
import {
  CampaignDetailPage,
  CampaignsPage,
} from "./routes/campaigns.js";
import { CampaignMembersPage } from "./routes/campaigns/MembersPage.js";
import {
  CustomerDetailPage,
  CustomersPage,
  validateCustomerDetailSearch,
  validateCustomersSearch,
} from "./routes/customers.js";
import { CustomerViewPage, validateCustomerViewSearch } from "./routes/customer-view.js";
import { CustomerViewStagePage } from "./routes/customer-view-stage.js";
import { DashboardPage } from "./routes/index.js";
import { InboxPage } from "./routes/inbox.js";
import { LoginPage } from "./routes/login.js";
import { SettingsPage, SettingsUsersPage } from "./routes/settings.js";
import { StatusReportsPage } from "./routes/statusberichte.js";
import {
  TeamDailyPage,
  validateTeamDailySearch,
} from "./routes/teamdaily.js";
import { VisitsPage, validateVisitsSearch } from "./routes/visits.js";
import { WartungPage, validateWartungSearch } from "./routes/wartung.js";
import { WeeklyPage, validateWeeklySearch } from "./routes/weekly.js";

// Root route — renders Outlet (child routes fill this)
const rootRoute = createRootRoute({
  component: () => createElement(Outlet),
});

// Login route — no auth guard
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

// Bühne der Customer View — Vollbild beim Kunden, deshalb direktes Kind der
// Root-Route: AuthGuard ja (wer nicht angemeldet ist, hat hier nichts zu
// suchen), AppLayout NEIN (keine Navigation, kein Banner, kein Cockpit-Chrom
// auf dem Beamer). Wer die Route zu den anderen unter `protectedRoute`
// einsortiert, bekommt das Layout stillschweigend zurück — dagegen steht der
// Wächtertest `router.stage.test.ts`.
const stageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/customer-view/stage",
  component: () => createElement(AuthGuard, null, createElement(CustomerViewStagePage)),
});

// Protected layout route — wraps all authenticated routes
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: () =>
    createElement(AuthGuard, null, createElement(AppLayout, null, createElement(Outlet))),
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: DashboardPage,
});

const customersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/customers",
  validateSearch: validateCustomersSearch,
  component: CustomersPage,
});

const customerDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/customers/$id",
  // Befund U9: aktiver Reiter (`tab`) und optional das aufzuklappende System
  // (`systemId`) stehen in der URL — sonst ist weder ein Rücksprung noch ein
  // Deep-Link auf ein einzelnes System möglich.
  validateSearch: validateCustomerDetailSearch,
  component: function CustomerDetailRoute() {
    const { id } = customerDetailRoute.useParams();
    return createElement(CustomerDetailPage, { id });
  },
});

const campaignsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/campaigns",
  component: CampaignsPage,
});

// Historischer Doppelweg: der Wizard lebt als Modal auf /campaigns. Die alte
// Route bleibt als Redirect erhalten (Bookmarks), rendert aber keine Seite mehr.
const campaignNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/campaigns/new",
  beforeLoad: () => {
    throw redirect({ to: "/campaigns" });
  },
});

const campaignDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/campaigns/$id",
  component: function CampaignDetailRoute() {
    const { id } = campaignDetailRoute.useParams();
    return createElement(CampaignDetailPage, { id });
  },
});

const campaignMembersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/campaigns/$id/members",
  component: function CampaignMembersRoute() {
    const { id } = campaignMembersRoute.useParams();
    return createElement(CampaignMembersPage, { campaignId: id });
  },
});

const bcgRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/bcg",
  validateSearch: validateBcgSearch,
  component: BcgPage,
});

const visitsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/visits",
  validateSearch: validateVisitsSearch,
  component: VisitsPage,
});

// Wartungsplanung: Leseansichten sind für die fünf Standardrollen freigegeben
// (Spec §6) — RequireRole fängt den direkten URL-Aufruf ab, der Nav-Link in
// AppLayout blendet den Punkt für andere Rollen ohnehin aus.
const wartungRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/wartung",
  validateSearch: validateWartungSearch,
  component: () =>
    createElement(RequireRole, {
      roles: [
        "geschaeftsleitung",
        "bereichsleiter",
        "stellvertreter",
        "projektleiter",
        "admin",
      ],
      children: createElement(WartungPage),
    }),
});

const inboxRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/inbox",
  component: () =>
    createElement(RequireRole, {
      roles: ["admin", "bereichsleiter", "stellvertreter"],
      children: createElement(InboxPage),
    }),
});

const kpiBereichRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/kpi/bereich",
  component: KpiBereichPage,
});

const kpiPortfolioRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/kpi/portfolio",
  component: KpiPortfolioPage,
});

const statusReportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/statusberichte",
  component: StatusReportsPage,
});

// TeamDaily — read-only, alle Rollen sehen es (kein RequireRole), wie die
// Statusberichte-Route.
const teamDailyRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/teamdaily",
  validateSearch: validateTeamDailySearch,
  component: TeamDailyPage,
});

// Weekly — alle angemeldeten Rollen; was jemand sieht, entscheidet die API.
const weeklyRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/weekly",
  validateSearch: validateWeeklySearch,
  component: WeeklyPage,
});

const customerViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/customer-view",
  validateSearch: validateCustomerViewSearch,
  component: CustomerViewPage,
});

const kpiAusreisserRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/kpi/ausreisser",
  component: KpiAusreisserPage,
});

// Inbox/Einstellungen liefern nur für Admin/BL/Stellv. Inhalte — Guard
// spiegelt das Nav-Gating (AppLayout) für direkte URL-Aufrufe.
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings",
  component: () =>
    createElement(RequireRole, {
      roles: ["admin", "bereichsleiter", "stellvertreter"],
      children: createElement(SettingsPage),
    }),
});

// Nutzerverwaltung ist die einzige Seite, die eine Rolle voraussetzt — der
// Nav-Link in AppLayout ist für Nicht-Admins ausgeblendet, der direkte
// URL-Aufruf muss hier abgefangen werden.
const settingsUsersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings/users",
  component: () =>
    createElement(RequireAdmin, null, createElement(SettingsUsersPage)),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  stageRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    customersRoute,
    customerDetailRoute,
    campaignsRoute,
    campaignNewRoute,
    campaignMembersRoute,
    campaignDetailRoute,
    bcgRoute,
    kpiBereichRoute,
    kpiPortfolioRoute,
    kpiAusreisserRoute,
    statusReportsRoute,
    teamDailyRoute,
    weeklyRoute,
    customerViewRoute,
    visitsRoute,
    wartungRoute,
    inboxRoute,
    settingsRoute,
    settingsUsersRoute,
  ]),
]);

export const router = createRouter({ routeTree });

// Register the router instance for type-safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
